import * as types from '../../actions/actiontypes';
import initialstate from '../initialstate';
import _ from 'lodash';
import {entities, HttpStatusCodes, ViewKeys, estimateTypes, TaskTypeEnum, AccountType} from '../../util/uiconstants';
import {isApiResponseOk, normalize, normalizedDelete, normalizedUpdate, concatMergeStrategy} from '../../util/utils';

function mapAccount(account) {
	const mappedAccount = {...account};

	if (account.assertions) {
		mappedAccount.assertions = account?.assertions?.map((assertion) => assertion);
	}

	if (account.entities) {
		mappedAccount.entities = account?.entities?.map((entity) => entity.id);
	}

	return mappedAccount;
}

/**
 * Deletes an account from a normalized state.
 * @param {Object} state Normalized accounts state.
 * @param {number} accountId Account id.
 * @returns	{Object} Updated normalized accounts state.
 */
function deleteAccount(state, accountId) {
	return normalizedDelete(state, accountId);
}

export function accountsReducer(state = initialstate.accounts, action) {
	let newState;

	switch (action.type) {
		case types.GET_ACCOUNTS:
		case types.LIST_ACCOUNTS:
			if (action.response && action.response.data) {
				newState = {...state};

				const accountList = normalize(action.response.data.map((account) => mapAccount(account)));
				accountList.pages = action.response.pages;
				accountList.page = action.response?.page;
				accountList.totalEntityCount = action.response.totalEntityCount;

				newState = {...accountList};

				return newState;
			}
			return state;
		case types.DELETE_ACCOUNT: {
			if (isApiResponseOk(action) || action.error.response.status === HttpStatusCodes.NotFound) {
				newState = {...state};

				// Delete the account from both manageAccounts and root slices
				if (newState.manageAccounts) {
					newState.manageAccounts = deleteAccount(newState.manageAccounts, action.accountId);
				}

				newState = deleteAccount(newState, action.accountId);

				return newState;
			}

			return state;
		}
		case types.CREATE_ACCOUNT: {
			if (isApiResponseOk(action) && action.response) {
				const newAccount = mapAccount(action.response);

				newState = normalize([newAccount], undefined, state);

				return newState;
			}
			return state;
		}
		case types.UPDATE_ACCOUNT:
			if (isApiResponseOk(action)) {
				newState = {...state};
				// Update account in both manageAccount and root slices:
				const accountPayload = mapAccount(action.response);

				if (newState.manageAccounts) {
					newState.manageAccounts = normalizedUpdate(accountPayload.id, accountPayload, newState.manageAccounts);
				}

				newState = normalizedUpdate(accountPayload.id, accountPayload, newState);

				return newState;
			}

			return state;
		// case types.UPDATE_MANAGE_ACCOUNT:
		// 	if (isApiResponseOk(action)) {
		// 		const accountPayload = mapAccount(action.response);
		// 		newState = {
		// 			...state,
		// 			byId: {
		// 				...state.byId,
		// 				[accountPayload.id]: {
		// 					...state.byId[accountPayload.id],
		// 					accountName: accountPayload.accountName
		// 				}
		// 			}
		// 		};

		// 		return newState;
		// 	}

		// 	return state;
		case types.GET_ACCOUNT_BY_ID: {
			if (isApiResponseOk(action)) {
				newState = {...state};
				const accountPayload = mapAccount(action.response);

				if (accountPayload?.entities === null) {
					accountPayload.entities = newState?.byId[action?.response?.id]?.entities;
				}
				newState = normalize([accountPayload], undefined, newState);

				return newState;
			}

			return state;
		}
		case types.SAVE_BATCH_HELIX_CANVAS_ACCOUNT_RELATIONS:
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				const returnedAccountList = action.response;
				const sentAccountList = state.sentAccounts;

				if (returnedAccountList.length < sentAccountList.length) {
					newState = {
						..._.cloneDeep(state),
						hasHelixAccountMappingsErrors: true
					};
					return newState;
				}
				return state;
			}
			return state;
		// eslint-disable-next-line no-case-declarations
		case types.SAVE_SENT_ASSOCIATED_HELIX_ACCOUNTS: {
			return {...state, sentAccounts: action.accountList};
		}
		case types.DELETE_DOCUMENT_BY_ID: {
			if (action.viewKey === ViewKeys.Account && !action.error) {
				return {
					...state,
					byId: {
						...state.byId,
						[action.entityUId]: {
							...state.byId[action.entityUId],
							relatedDocuments: state.byId[action.entityUId]?.relatedDocuments?.filter((doc) => doc.id !== action.documentId)
						}
					}
				};
			}
			return state;
		}
		case types.RELATE_ENTITY_ACCOUNT: {
			if (isApiResponseOk(action)) {
				const entityIds = state.byId[action.accountId].entities;

				const currentEntities = entityIds?.includes(action.stEntityId)
					? [...(state.byId[action.accountId].entities || [])]
					: [...(state.byId[action.accountId].entities || []), action.stEntityId];

				return {
					...state,
					byId: {
						...state.byId,
						[action.accountId]: {
							...state.byId[action.accountId],
							entities: currentEntities
						}
					}
				};
			}

			return state;
		}
		case types.UNRELATE_ENTITY_ACCOUNT: {
			if (isApiResponseOk(action)) {
				return {
					...state,
					byId: {
						...state.byId,
						[action.accountId]: {
							...state.byId[action.accountId],
							entities: state.byId[action.accountId].entities.filter((entity) => entity !== action.stEntityId)
						}
					}
				};
			}

			return state;
		}
		case types.CREATE_ENTITY_FORM: {
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				const accountId = action.objectIds[0];
				const relatedDocument = {
					accountID: accountId,
					id: action.response[0]?.documentID,
					documentName: action.response[0]?.documentName,
					documentTypeID: action.documentTypeId,
					fileExt: 'smt'
				};

				const relatedDocuments =
					state.byId[accountId]?.relatedDocuments && state.byId[accountId]?.relatedDocuments.length > 0
						? [...state.byId[accountId]?.relatedDocuments, relatedDocument]
						: [relatedDocument];

				return {
					...state,
					byId: {
						...state.byId,
						[accountId]: {
							...state.byId[accountId],
							relatedDocuments: relatedDocuments
						}
					}
				};
			}
			return state;
		}
		case types.GET_TOP_ENTITY_METADATA: {
			if (action.response && action.entity && action.entity === entities.Account) {
				const accountId = action.response.id;

				if (state.allIds.includes(accountId) && action.includeAssertions) {
					return {
						...state,
						byId: {
							...state.byId,
							[accountId]: {
								...state.byId[accountId],
								assertions: action.response.assertions
							}
						}
					};
				} else {
					newState = {...state};
					const accountPayload = mapAccount(action.response);

					if (accountPayload?.entities === null) {
						accountPayload.entities = newState?.byId[action?.response?.id]?.entities;
					}
					newState = normalize([accountPayload], undefined, newState);

					return newState;
				}
			}

			return state;
		}
		case types.CREATE_TASK: {
			if (isApiResponseOk(action) && action.accountId) {
				const accountId = action.accountId;
				const account = state.byId[accountId];
				const accountTypeId = account?.accountTypeId;

				if (!account) {
					return state;
				}

				// For limited risk accounts (accountTypeId === 2), add task directly without assertions
				if (accountTypeId === AccountType.LimitedRisk) {
					const limitedRiskAccountTask = {
						taskId: action.response.id,
						taskName: action.meta.api.data.title,
						taskDisplayName: action.meta.api.data.title,
						isRejected: false,
						taskStatusId: 1,
						taskTypeId: action.meta.api.data.taskTypeId,
						accountID: accountId
					};

					const updatedAccountTasks = account?.accountTasks
						? [...account.accountTasks, limitedRiskAccountTask]
						: [limitedRiskAccountTask];

					return {
						...state,
						byId: {
							...state.byId,
							[accountId]: {
								...account,
								accountTasks: updatedAccountTasks
							}
						}
					};
				}

				// For other account types, create assertions and add assertion tasks
				const assertionIds = action.meta.api.data.assertionIds || [];
				const engagementID = action.meta.api.data.engagementId || account?.engagementID;

				// Create new assertions for any assertionIds that don't exist in account.assertions
				const existingAssertionIds = (account?.assertions || []).map((a) => a.id || a.assertionId);
				const newAssertions = assertionIds
					.filter((assertionId) => !existingAssertionIds.includes(assertionId))
					.map((assertionId) => ({
						accountID: accountId,
						controlRiskTypeId: null,
						craValue: '',
						displayKnowledgeAssertionId: assertionId,
						engagementID: engagementID,
						hasRiskAssociated: false,
						id: assertionId,
						inherentRiskTypeId: null,
						knowledgeAssertionId: assertionId,
						risks: [],
						wcgWs: null,
						wcgwTasks: null
					}));

				// Merge existing and new assertions
				const updatedAssertions = [...(account?.assertions || []), ...newAssertions];

				// Map assertion IDs to assertion objects from the updated assertions
				const taskAssertions = assertionIds
					.map((assertionId) => updatedAssertions.find((a) => a.id === assertionId || a.assertionId === assertionId))
					.filter(Boolean);

				// Create separate task entries for assertionTasks (one per assertion)
				// This is required for getPspOrOspTasks to properly group and display them
				const assertionTasksList = assertionIds.map((assertionId) => ({
					taskId: action.response.id,
					taskName: action.meta.api.data.title,
					taskDisplayName: action.meta.api.data.title,
					isRejected: false,
					taskStatusId: 1,
					taskTypeId: action.meta.api.data.taskTypeId,
					accountID: accountId,
					assertionID: assertionId
				}));

				// Only add to assertionTasks to avoid duplicates in getPspOrOspTasks
				const updatedAssertionTasks = account?.assertionTasks
					? [...account.assertionTasks, ...assertionTasksList]
					: assertionTasksList;

				return {
					...state,
					byId: {
						...state.byId,
						[accountId]: {
							...account,
							assertionTasks: updatedAssertionTasks,
							assertions: updatedAssertions
						}
					}
				};
			} else {
				return state;
			}
		}

		case types.REJECT_TASK: {
			if (action.error) return state; // proceed only if API responded
			const meta = action.payload[1];
			const additionalInfo = action.payload[0].value.additionalInfo;
			const account = state.byId[meta.accountId];
			const updatedTasks = account.assertionTasks.map((t) => {
				return t.taskId === meta.taskId ? {...t, isRejected: true, additionalInfo: additionalInfo} : t;
			});
			return {
				...state,
				byId: {
					...state.byId,
					[meta.accountId]: {
						...account,
						assertionTasks: updatedTasks
					}
				}
			};
		}
		case types.ACCEPT_TASK: {
			if (action.error) return state;
			const meta = action.payload[1];
			const account = state.byId[meta.accountId];
			const updatedTasks = account.assertionTasks.map((t) => {
				return t.taskId === meta.taskId ? {...t, isRejected: false, additionalInfo: ''} : t;
			});
			return {
				...state,
				byId: {
					...state.byId,
					[meta.accountId]: {
						...account,
						assertionTasks: updatedTasks
					}
				}
			};
		}
		case types.REMOVE_TASK: {
			if (action.error) return state;
			const selectedAccountId = action?.payload?.accountID;
			const taskId = action?.payload?.taskId;
			if (!taskId || !selectedAccountId) return state;

			const selectedAccount = state.byId[selectedAccountId];
			if (!selectedAccount) return state;

			// Remove from accountTasks if it exists there
			const updatedAccountTasks = selectedAccount.accountTasks ? selectedAccount.accountTasks.filter((t) => t.taskId !== taskId) : selectedAccount.accountTasks;

			// Remove from assertionTasks if it exists there
			const updatedAssertionTasks = selectedAccount.assertionTasks
				? selectedAccount.assertionTasks.filter((t) => t.taskId !== taskId)
				: selectedAccount.assertionTasks;

			return {
				...state,
				byId: {
					...state.byId,
					[selectedAccountId]: {
						...selectedAccount,
						accountTasks: updatedAccountTasks,
						assertionTasks: updatedAssertionTasks
					}
				}
			};
		}
		case types.UPDATE_TASK_ASSERTION: {
			if (action.error) return state;
			const updateAssertionAccountId = action.payload?.accountID;
			const taskId = action.payload?.taskId;
			const assertionId = action.meta?.api?.data || 0;

			if (!updateAssertionAccountId || !taskId) return state;

			const updateAssertionAccount = state.byId[updateAssertionAccountId];
			if (!updateAssertionAccount) return state;

			// Create task entry for single assertion
			const newAssertionTask = {
				taskId: taskId,
				taskName: action.payload?.title,
				isRejected: false,
				taskStatusId: 1,
				taskTypeId: TaskTypeEnum.OTHER_SUBSTANTIVE_PROCEDURE_TASK,
				accountID: updateAssertionAccountId,
				taskDisplayName: action.payload?.taskDisplayName,
				assertionID: assertionId
			};

			return {
				...state,
				byId: {
					...state.byId,
					[updateAssertionAccountId]: {
						...updateAssertionAccount,
						assertionTasks: [...(updateAssertionAccount.assertionTasks || []), newAssertionTask]
					}
				}
			};
		}
		case types.DELETE_TASK_ASSERTION: {
			if (action.error) return state;
			const deleteAssertionAccountId = action.payload?.accountID;
			const deleteAssertionTaskId = action.payload?.taskId;
			const deleteAssertionId = action.payload?.assertionId;

			if (!deleteAssertionAccountId || !deleteAssertionTaskId || !deleteAssertionId) return state;

			const deleteAssertionAccount = state.byId[deleteAssertionAccountId];
			if (!deleteAssertionAccount) return state;

			// Remove the specific assertion from assertionTasks for this task
			const undeletedAssertionTasks = deleteAssertionAccount.assertionTasks.filter(
				(t) => !(t.taskId === deleteAssertionTaskId && t.assertionID === deleteAssertionId)
			);

			return {
				...state,
				byId: {
					...state.byId,
					[deleteAssertionAccountId]: {
						...deleteAssertionAccount,
						assertionTasks: undeletedAssertionTasks
					}
				}
			};
		}
		case types.DELETE_CONTROL_BY_ID: {
			const controlId = action.controlId;
			if (!controlId) return state;

			return {
				...state,
				byId: Object.fromEntries(
					Object.entries(state.byId).map(([key, item]) => [
						key,
						{
							...item,
							riskControls: item.riskControls?.filter((control) => control.controlId !== controlId) || []
						}
					])
				)
			};
		}
		case types.UPDATE_CONTROL: {
			const controlId = action.response?.id;
			if (!controlId) return state;

			const controlUpdates = {
				hasControlTesting: action.response.hasControlTesting,
				isEffective: action.response.isEffective,
				isIPEManuallyTested: action.response.isIPEManuallyTested,
				isLowerRisk: action.response.isLowerRisk,
				controlName: action.response.controlName,
				controlFrequencyTypeId: action.response.controlFrequencyTypeId,
				controlTypeId: action.response.controlTypeId,
				ITApplicationOrSOIds: action.response.itApplicationOrSOIds,
				controlDesignEffectivenessTypeId: action.response.controlDesignEffectivenessTypeId
			};

			return {
				...state,
				byId: Object.fromEntries(
					Object.entries(state.byId).map(([key, item]) => [
						key,
						{
							...item,
							riskControls: item.riskControls?.map((control) => (control.controlId === controlId ? {...control, ...controlUpdates} : control)) || []
						}
					])
				)
			};
		}
		case types.APPEND_SCOT_TO_ACCOUNT: {
			if (action.payload && action.payload.data) {
				const accountId = action.payload.data.accountID;
				const scot = action.payload.data;

				newState = {...state};

				// Update account in main slice
				if (newState.byId && newState.byId[accountId]) {
					const updatedScots = [...(newState.byId[accountId].scots || []), scot];
					newState.byId[accountId] = {
						...newState.byId[accountId],
						scots: _.uniqBy(updatedScots, 'id')
					};
				}

				// Also update manageAccounts slice if it exists
				if (newState.manageAccounts && newState.manageAccounts.byId && newState.manageAccounts.byId[accountId]) {
					const updatedScots = [...(newState.manageAccounts.byId[accountId].scots || []), scot];
					newState.manageAccounts.byId[accountId] = {
						...newState.manageAccounts.byId[accountId],
						scots: _.uniqBy(updatedScots, 'id')
					};
				}

				return newState;
			}
			return state;
		}
		case types.DELETE_SCOT_FROM_ACCOUNT: {
			if (action.payload && action.payload.data) {
				const {accountID, id} = action.payload.data;

				newState = {...state};

				// Update account in main slice
				if (newState.byId && newState.byId[accountID]) {
					const updatedScots = (newState.byId[accountID].scots || []).filter((scot) => scot.id !== id);
					newState.byId[accountID] = {
						...newState.byId[accountID],
						scots: updatedScots
					};
				}

				// Also update manageAccounts slice if it exists
				if (newState.manageAccounts && newState.manageAccounts.byId && newState.manageAccounts.byId[accountID]) {
					const updatedScots = (newState.manageAccounts.byId[accountID].scots || []).filter((scot) => scot.id !== id);
					newState.manageAccounts.byId[accountID] = {
						...newState.manageAccounts.byId[accountID],
						scots: updatedScots
					};
				}

				return newState;
			}
			return state;
		}
		default:
			return state;
	}
}

export function accountsEditReducer(state = initialstate.accounts, action) {
	switch (action.type) {
		case types.GET_ACCOUNTS_EDIT:
			if (action.response) {
				return action.response.data || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		default:
			return state;
	}
}

export function accountAssertionsReducer(state = initialstate.accountAssertions, action) {
	switch (action.type) {
		case types.GET_ASSERTIONS_FOR_ACCOUNTID:
			if (action.response) {
				return action.response || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		default:
			return state;
	}
}

export function accountDetailsReducer(state = initialstate.accountDetails, action) {
	switch (action.type) {
		case types.GET_REVIEWS_FOR_ACCOUNTID:
			if (action.response) {
				return action.response || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		default:
			return state;
	}
}

export function removeAccountReviewReducer(state = initialstate.accountDetails, action) {
	switch (action.type) {
		case types.DELETE_REVIEWS_FOR_ACCOUNTID:
			return state;
	}
}

export function addAccountReviewReducer(state = initialstate.accountDetails, action) {
	switch (action.type) {
		case types.ADD_REVIEWS_FOR_ACCOUNTID:
			return action.response;
	}
}

export function currentAccountReducerSEM(state = initialstate.currentAccountSEM, action) {
	switch (action.type) {
		case types.GET_ASSERTIONS_FOR_ACCOUNTID_SEM:
			if (action.response) {
				return action.response || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		default:
			return state;
	}
}

// This reducer processes the list of accounts and formats each account to include a combined, deduplicated, and sorted array
// of associated Scots and Risks (estimates).
export function accountsWithCombinedScotsAndRisksReducer(state = initialstate.scotsAndRisksAccountList, action) {
	switch (action.type) {
		case types.GET_ACCOUNTS:
			if (action.response && action.response.data && action.isFormattedAccounts) {
				const formattedList = action.response.data.map((account) => {
					const risks = (account.assertions || [])
						.flatMap((assertion) =>
							(assertion.risks || []).map((risk) => {
								if (_.includes([estimateTypes.higher, estimateTypes.low], risk.riskTypeId)) {
									return {
										id: risk.id,
										riskName: risk.riskName,
										riskTypeId: risk.riskTypeId
									};
								}
							})
						)
						?.filter(Boolean);

					let distinctScots = _.orderBy(_.uniqBy(account?.scots, 'id'), ['scotName'], ['asc']);
					let distictEstimates = _.orderBy(_.uniqBy(risks, 'id'), ['name'], ['asc']);

					return {
						id: account.id,
						accountName: account.accountName,
						hasEstimate: account.hasEstimate,
						executionTypeId: account.executionTypeId,
						relatedScotsLength: distinctScots?.length || 0,
						relatedEstimatesLength: distictEstimates?.length || 0,
						scots: distinctScots,
						estimates: distictEstimates
					};
				});

				return {
					...action.response,
					data: formattedList
				};
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		case types.APPEND_SCOT_TO_ACCOUNT:
			if (action.payload && action.payload.data) {
				const accountId = action.payload.data.accountID;
				const scot = action.payload.data;

				if (state.data && state.data.length > 0) {
					const updatedAccounts = state.data.map((account) => {
						if (account.id === accountId) {
							const updatedScots = [...(account.scots || []), scot];
							return {
								...account,
								relatedScotsLength: account.relatedScotsLength + 1,
								scots: _.uniqBy(updatedScots, 'id')
							};
						}
						return account;
					});

					return {
						...state,
						data: updatedAccounts
					};
				}
			}
		case types.DELETE_SCOT_FROM_ACCOUNT:
			if (action.payload && action.payload.data) {
				const {accountID, id} = action.payload.data;

				if (state.data && state.data.length > 0) {
					const updatedAccounts = state.data.map((account) => {
						if (account.id === accountID) {
							const updatedScots = (account.scots || []).filter((scot) => scot.id !== id);
							return {
								...account,
								relatedScotsLength: account.relatedScotsLength - 1,
								scots: _.uniqBy(updatedScots, 'id')
							};
						}
						return account;
					});

					return {
						...state,
						data: updatedAccounts
					};
				}
			}
		default:
			return state;
	}
}

//TODO: move to a common function in utils.
export function formatAssertionsForDisplayInWcgwTask(assertions, checkedAssertions = true) {
	let assertionsToUpdate = _.cloneDeep(assertions);

	if (assertions && assertions.length === 5) {
		if (checkedAssertions === false) {
			return assertionsToUpdate;
		} else {
			assertionsToUpdate.map((assertion) => {
				assertion.checked = false;
			});
		}

		return assertionsToUpdate;
	} else if (assertions && assertions.length > 0 && assertions.length < 5) {
		if (checkedAssertions === true) {
			assertionsToUpdate.map((assertion) => {
				assertion.checked = false;
			});
		}
		let assertionKnowledgeIds = assertionsToUpdate.map((a) => a.knowledgeAssertionId),
			emptyAssertion = {
				assertionName: '',
				assertionAbbreviation: '',
				accountId: 0,
				assertionId: 0,
				inherentRiskTypeId: 0,
				controlRiskTypeId: 0,
				hasRiskAssociated: false,
				accountName: null,
				knowledgeAssertionId: 6,
				craValue: '',
				validationStatus: null,
				validationMessages: null,
				displayOrder: 0,
				id: 0,
				notForDisplay: true
			};

		if (!assertionKnowledgeIds.some((v) => [6, 1, 10].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(0, 0, emptyAssertion);
		}
		if (!assertionKnowledgeIds.some((v) => [7, 2, 11].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(1, 0, emptyAssertion);
		}
		if (!assertionKnowledgeIds.some((v) => [8, 3, 12].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(2, 0, emptyAssertion);
		}
		if (!assertionKnowledgeIds.some((v) => [4, 13].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(3, 0, emptyAssertion);
		}
		if (!assertionKnowledgeIds.some((v) => [9, 5, 14].indexOf(v) !== -1)) {
			assertionsToUpdate.splice(4, 0, emptyAssertion);
		}
		return assertionsToUpdate;
	}
}

export function accountReducer(state = initialstate.account, action) {
	let newState;

	switch (action.type) {
		case types.CREATE_MANAGE_ACCOUNT:
		case types.CREATE_ACCOUNT:
			if (action.response && !action.error) {
				newState = _.cloneDeep(state);
				newState = action.response;
				return newState;
			}
			return state;
		case types.GET_ACCOUNT_BY_ID:
			if (action.response) {
				return action.response || state;
			} else {
				return {
					errorMessage: action.meta.auth.error
				};
			}
		case types.CLEAN_ACCOUNT:
			return {};
		case types.UPDATE_ACCOUNT:
		case types.UPDATE_MANAGE_ACCOUNT:
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				let account = _.cloneDeep(state);
				if (account.id === action.response.id) {
					account.accountTypeId = action.response.accountTypeId;
				}
				account = {...account, ...action.response};

				const responseAssertionsHaveRisks = action.response.assertions?.some(
					(a) => (Array.isArray(a.risks) ? a.risks.length > 0 : !!a.risks)
				);
				if (!responseAssertionsHaveRisks) {
					account.assertions = state.assertions;
				}

				return account;
			}
			return state;
		case types.UPDATE_STORE_ACCOUNT_ASSERTION: {
			const account = _.cloneDeep(state);
			account.assertions = action.payload?.map((item) => ({id: item.assertionId}));
			return account;
		}
		case types.DELETE_ACCOUNT:
			return {};
		case types.GET_TOP_ENTITY_METADATA: {
			if (action.response && action.entity && action.entity === entities.Account) {
				return {
					...action.response
				};
			}

			return state;
		}
		case types.PATCH_ACCOUNT_ATTRIBUTES: {
			if (action.response) {
				return {
					...action.response
				};
			}
			return state;
		}
		case types.UPDATE_CONTROL: {
			const controlId = action.response?.id;
			if (!controlId || !state || !Array.isArray(state.riskControls)) {
				return state;
			}

			const controlUpdates = {
				hasControlTesting: action.response.hasControlTesting,
				isEffective: action.response.isEffective,
				isIPEManuallyTested: action.response.isIPEManuallyTested,
				isLowerRisk: action.response.isLowerRisk,
				controlName: action.response.controlName,
				controlFrequencyTypeId: action.response.controlFrequencyTypeId,
				controlTypeId: action.response.controlTypeId,
				ITApplicationOrSOIds: action.response.itApplicationOrSOIds,
				controlDesignEffectivenessTypeId: action.response.controlDesignEffectivenessTypeId
			};

			const updatedRiskControls = state.riskControls.map((control) => (control.controlId === controlId ? {...control, ...controlUpdates} : control));

			return {
				...state,
				riskControls: updatedRiskControls
			};
		}

		default:
			return state;
	}
}

export function areAllCanvasAccountsMappedToHelixAccountsReducer(state = initialstate.helixAccountsMappingStatus, action) {
	switch (action.type) {
		case types.GET_IS_HELIX_CANVAS_ACCOUNT_MAPPINGS_SUCCESSFULL:
			return action.response?.allCanvasAccountIncludeInHelixAccount && action.response?.allHelixAccountsIncludeInCanvasAccounts;
		default:
			return state;
	}
}
